// コストの台帳。GET /costs/range で届く raw_data ごとのコストを、画面に流れた確定イベントと
// rawDataId で突き合わせ、会社 × 課金単位 (枚数課金 / 件数課金) ごとに積む。
//
// コストはイベントより遅れて届く (クエリが重い) ので、どちらが先に来ても合うようにしてある:
//   - 確定イベントが先: settled に覚えておき、コストが届いた時点で積む
//   - コストが先:       costs に覚えておき、確定イベントが流れた時点で積む
// 画面に流れていない (= 省くモードで落とした / まだ cursor が届いていない) raw_data のコストは積まない。

import { BillingType, RawDataCost, isRawDataCost } from '../types/events.js';
import { API_BASE } from './config.js';
import { authFetch } from './auth.js';

export interface CostTally {
  usd: number;
  units: number;
}

export type CostByBillingType = Readonly<Record<BillingType, CostTally>>;

/** 行やカードに出す順。 */
export const BILLING_TYPES: ReadonlyArray<BillingType> = ['per_page', 'per_case'];

const UNIT_LABELS: Readonly<Record<BillingType, string>> = {
  per_page: '枚',
  per_case: '件',
};

/** 失敗した取得をやり直す回数と間隔。初回は DB のキャッシュが冷えていて時間切れになりやすい。 */
const MAX_ATTEMPTS = 3;
const RETRY_DELAY_MS = 20_000;

interface Span {
  readonly fromMs: number;
  readonly toMs: number;
}

function emptyTallies(): Record<BillingType, CostTally> {
  return { per_page: { usd: 0, units: 0 }, per_case: { usd: 0, units: 0 } };
}

/** span から covered を引いた残りの区間を返す。 */
function subtractSpans(span: Span, covered: ReadonlyArray<Span>): Span[] {
  let rest: Span[] = [span];
  for (const c of covered) {
    const next: Span[] = [];
    for (const r of rest) {
      if (c.toMs <= r.fromMs || c.fromMs >= r.toMs) {
        next.push(r);
        continue;
      }
      if (c.fromMs > r.fromMs) next.push({ fromMs: r.fromMs, toMs: c.fromMs });
      if (c.toMs < r.toMs) next.push({ fromMs: c.toMs, toMs: r.toMs });
    }
    rest = next;
  }
  return rest;
}

/**
 * "$0.069/枚" の形。分母がまだ 0 なら割らずに "$1.23 · 0枚" (費用の合計と分母) を出す。
 * 費用も分母も無ければ null。
 */
export function formatUnitCost(billingType: BillingType, tally: CostTally): string | null {
  const unit = UNIT_LABELS[billingType];
  if (tally.units <= 0) return tally.usd > 0 ? `$${tally.usd.toFixed(2)} · 0${unit}` : null;
  const perUnit = tally.usd / tally.units;
  return `$${perUnit.toFixed(perUnit < 0.01 ? 4 : 3)}/${unit}`;
}

export class CostLedger {
  /** 取得済みのコスト。seek しても捨てない (同じ区間を取り直さないため)。 */
  private readonly costs: Map<string, RawDataCost> = new Map();
  /** 取得済み (取得中を含む) の区間。 */
  private covered: Span[] = [];
  /** 画面に流れた確定 raw_data → 会社名。seek で作り直す。 */
  private readonly settled: Map<string, string> = new Map();
  /** 集計に積み終えたコスト (積んだ時点の値)。取り直しで値が変わったら差し替える。seek で作り直す。 */
  private readonly applied: Map<string, RawDataCost> = new Map();
  private readonly byCompany: Map<string, Record<BillingType, CostTally>> = new Map();
  private total: Record<BillingType, CostTally> = emptyTallies();

  /** 画面を作り直すとき (seek / 日付変更 / 省く・含むの切替) に呼ぶ。取得済みのコストは残す。 */
  reset(): void {
    this.settled.clear();
    this.applied.clear();
    this.byCompany.clear();
    this.total = emptyTallies();
  }

  /** 確定イベントが画面に流れたときに呼ぶ。 */
  noteSettled(rawDataId: string, companyName: string): void {
    if (this.settled.has(rawDataId)) return;
    this.settled.set(rawDataId, companyName);
    this.apply(rawDataId);
  }

  /** イベントを取り終えた区間について、まだ取っていない部分のコストを取りに行く (待たない)。 */
  requestRange(fromMs: number, toMs: number): void {
    // 先読みは未来の時刻まで取りに行く。未来を取得済みにすると、その時刻が来たときに取り直せないので今で切る。
    const untilMs = Math.min(toMs, Date.now());
    if (untilMs <= fromMs) return;
    for (const span of subtractSpans({ fromMs, toMs: untilMs }, this.covered)) {
      this.covered.push(span);
      void this.load(span, 1);
    }
  }

  getCompany(companyName: string): CostByBillingType | null {
    return this.byCompany.get(companyName) ?? null;
  }

  getTotal(): CostByBillingType {
    return this.total;
  }

  private apply(rawDataId: string): void {
    const cost = this.costs.get(rawDataId);
    const companyName = this.settled.get(rawDataId);
    if (cost === undefined || companyName === undefined) return;
    // 再実行などで同じ raw_data のコストが取り直されることがある。前に積んだ値を引いてから積み直す。
    const previous = this.applied.get(rawDataId);
    if (previous === cost) return;
    let company = this.byCompany.get(companyName);
    if (company === undefined) {
      company = emptyTallies();
      this.byCompany.set(companyName, company);
    }
    if (previous !== undefined) this.add(company, previous, -1);
    this.add(company, cost, 1);
    this.applied.set(rawDataId, cost);
  }

  private add(company: Record<BillingType, CostTally>, cost: RawDataCost, sign: 1 | -1): void {
    for (const tally of [company[cost.billingType], this.total[cost.billingType]]) {
      tally.usd += sign * cost.usd;
      tally.units += sign * cost.units;
    }
  }

  private async load(span: Span, attempt: number): Promise<void> {
    const fromIso = new Date(span.fromMs).toISOString();
    const toIso = new Date(span.toMs).toISOString();
    try {
      const res = await authFetch(
        `${API_BASE}/costs/range?from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}`,
      );
      if (!res.ok) throw new Error(`costs ${res.status}`);
      const parsed: unknown = await res.json();
      if (!Array.isArray(parsed)) throw new Error('costs: not an array');
      for (const item of parsed) {
        if (!isRawDataCost(item)) continue;
        this.costs.set(item.rawDataId, item);
        this.apply(item.rawDataId);
      }
    } catch {
      if (attempt < MAX_ATTEMPTS) {
        window.setTimeout(() => void this.load(span, attempt + 1), RETRY_DELAY_MS);
        return;
      }
      // 諦めた区間は未取得に戻す。次に同じ区間のイベントを取ったときに取り直す。
      this.covered = this.covered.filter((c) => c !== span);
    }
  }
}
